import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useTheme } from '../theme-context';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import Sidebar from './layout-components/Sidebar';
import RightSidebar from './layout-components/RightSidebar';
import ChatDrawer from '../components/search-chat/chat';
import SearchInput from '../shared/chat-bar/search-input';
import { CHAT_ACTIONS } from '../utils/constants/chat-actions';
import NewChatModal from '../components/new-chat/NewChatModal';
import { tryToFetchTeamMembersSidebar } from '../store/brokers/partners/teams/teamsSlice';
import { tryToCreateChatMessage } from '../store/brokers/partners/chats/ChatsSlice';
import { AI_AGENTS } from '../utils/constants/ai-agents';
import { openChat, closeChat } from '../store/brokers/partners/chats/ChatsUISlice';
import { PartnerGuard } from '../guards/RolesGuard';


const Layout = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const { theme } = useTheme();
    const chatUi = useAppSelector((state) => state.chatUi);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
    const [isRightSidebarCollapsed, setIsRightSidebarCollapsed] = useState(true);
    const [chatIsWaiting, setChatIsWaiting] = useState(false);
    const [chatData, setChatData] = useState<any>();
    const [showSearchChat, setShowSearchChat] = useState(false);
    const [showAdditionals, setShowAdditionals] = useState<any>();

    const onOpenChat = async (data?: any, chatId?: string) => {
        try {
            setSidebarOpen(false);
            setChatIsWaiting(true);
            if (!chatId && !data) {
                await dispatch(closeChat());
            }
            if (data) {
                const formatData = {
                    message: data?.user?.message,
                    files: data?.user?.files
                };
                const response = await dispatch(tryToCreateChatMessage(formatData)).unwrap();
                const newChatData = [
                    { user: data?.user },
                    {
                        agent: {
                            ...response?.data || {},
                            message: response?.data?.message,
                            files: [],
                            createdAt: new Date(),
                            _id: response?.data?.agentMessageId,
                        }
                    }
                ];
                setChatData(newChatData);

                dispatch(openChat({
                    chatId: response?.data?.topic || chatId,
                    data: newChatData
                }));
            } else {
                setChatData(undefined);

                dispatch(openChat({
                    chatId: chatId,
                    data: []
                }));
            }
        } catch (err) {
            console.error(err);
        }

        setChatIsWaiting(false);
    };

    const onClickAction = (action: any) => {
        setRightSidebarOpen(false);
        const formatUserRequest = {
            user: {
                message: action.text,
                files: [],
                sort: 2
            }
        };

        setChatData([]);
        dispatch(openChat({ data: [formatUserRequest], action }));
    };


    const onClickShowModal = () => {
        setSidebarOpen(false);
        setShowSearchChat(!showSearchChat);
    };

    const onTryingToOpenChat = (id?: string, action?: any) => {
        setShowSearchChat(false);
        if (action) {
            onClickAction(action);
        } else {
            dispatch(openChat({ chatId: id, data: [] }));
        }
        setChatData([]);
    };

    const onGettingData = async () => {
        try {
            const data = {
                pageSize: 5,
                pageNumber: 1
            };
            await dispatch(tryToFetchTeamMembersSidebar(data)).unwrap();
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        if (!chatUi.open) {
            onGettingData();
        }
    }, [chatUi.open]);

    useEffect(() => {
        if (
            chatUi.open &&
            !chatUi.chatData?.length &&
            !chatUi.action &&
            chatUi.chatId
        ) {
            onOpenChat(undefined, chatUi.chatId);
        }
    }, [chatUi]);

    const onCloseChat = () => {
        dispatch(closeChat());
        setChatData(undefined);
    };

    const onClickAdditional = (action: string) => {
        setShowAdditionals(action === showAdditionals ? undefined : action);
    };

    return (
        <div className='bg-white flex flex-col min-h-screen'>
            {sidebarOpen && (
                <div className="fixed inset-0 z-40 flex md:hidden">
                    <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setSidebarOpen(false)}></div>
                    <div className="relative bg-white w-70 h-full shadow-xl z-50 flex flex-col">
                        <div className="flex justify-end p-3 pr-[10px] flex-shrink-0">
                            <button onClick={() => setSidebarOpen(false)}>
                                <img src="/assets/shared/close-grey.svg" alt="Close" className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            <Sidebar onClickShowModal={onClickShowModal} onClickOpenChat={onOpenChat} isMobile={true} />
                        </div>
                    </div>
                </div>
            )}
            <Sidebar onClickShowModal={onClickShowModal} onClickOpenChat={onOpenChat} />
            <div className={`flex flex-col flex-1 ${isRightSidebarCollapsed ? 'md:pl-64 md:pr-16' : 'md:px-64'}`}>
                <header className='sticky top-0 z-10 flex-shrink-0 flex bg-white dark:bg-bgColor-darkMode p-4 h-16 md:hidden justify-between'>
                    <button
                        type='button'
                        className='text-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 px-1'
                        onClick={() => setSidebarOpen(true)}
                    >
                        <img src={theme === 'dark' ? '/assets/navigation-icons/burger-menu-white.svg' : '/assets/navigation-icons/burger-menu.svg'} className='object-contain w-[23px]' />
                    </button>
                    <button
                        type='button'
                        className='text-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 px-1'
                        onClick={() => setRightSidebarOpen(true)}
                    >
                        <img src={theme === 'dark' ? '/assets/navigation-icons/left-side-arrow-white.svg' : '/assets/navigation-icons/left-side-arrow.svg'} className='object-contain w-[23px]' />
                    </button>
                </header>
                <main className='flex-1 relative'>
                    <AnimatePresence mode="wait">
                        {chatUi.open && location?.pathname === '/insights' ? (
                            <ChatDrawer
                                key="chat"
                                isOpen={chatUi.open}
                                chatData={chatUi.chatData}
                                action={chatUi.action}
                                historyId={chatUi.chatId}
                                onClose={onCloseChat}
                                triggerId="charles-chat"
                            />
                        ) : (
                            <motion.div
                                key="input"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.3 }}
                            >
                                {location?.pathname === '/insights' && PartnerGuard() && (
                                    <div className='py-5 px-4 bg-[#fff] dark:bg-[#191919] min-w-full border-b border-b-1'>
                                        <div className='mb-5'>
                                            {AI_AGENTS?.map((item, idx) => (
                                                <div key={idx} className='border-l border-l-4 rounded-sm border-[#1E4380] dark:border-borderColor-darkMode pl-2 flex flex-row items-center'>
                                                    <p className='dark:text-primary-dark mr-2'>{item.name}</p>
                                                    <p className='text-primary-light text-sm'>{item.description}</p>
                                                </div>
                                            ))}
                                        </div>
                                        <SearchInput
                                            placeholder={'Ask something...'}
                                            chatData={chatData || []}
                                            chatId={chatUi.chatId}
                                            considerDarkMode={true}
                                            isWaiting={chatIsWaiting}
                                            onSendingData={onOpenChat}
                                        />
                                        <div className='flex flex-row items-center my-3 px-4'>
                                            <div className='flex flex-row items-center cursor-pointer mr-6' onClick={() => onClickAdditional('actions')}>
                                                <p className='text-[#535862] dark:text-primary-dark mr-2'>Actions</p>
                                                <img src={theme === 'dark' ? '/assets/insights/see-more-icon-white.svg' : '/assets/layout/dots-gray.svg'} className='object-container w-[20px]' />
                                            </div>
                                            {/* <div className='flex flex-row items-center cursor-pointer' onClick={() => onClickAdditional('apps')}>
                                                <p className='text-[#535862] dark:text-primary-dark mr-2'>Connected Apps</p>
                                                <img src={theme === 'dark' ? '/assets/insights/see-more-icon-white.svg' : '/assets/layout/dots-gray.svg'} className='object-container w-[20px]' />
                                            </div> */}
                                        </div>
                                        {showAdditionals === 'actions' && (
                                            <div className='flex flex-row items-center flex-wrap gap-2'>
                                                {CHAT_ACTIONS?.map((item, idx) => (
                                                    <div
                                                        key={idx}
                                                        onClick={() => onClickAction(item)}
                                                        className='mr-1 rounded-full cursor-pointer hover:opacity-80 py-1 px-3 bg-[#252B37] dark:bg-black text-white text-center'
                                                    >
                                                        <p className='font-bold text-sm'>{item.name}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className='px-4 py-4 dark:bg-bgColor-darkMode'>
                                    <Outlet />
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </main>
            </div>
            {rightSidebarOpen && (
                <div className="fixed inset-0 z-40 flex md:hidden">
                    <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setRightSidebarOpen(false)}></div>
                    <div className="relative bg-white dark:bg-bgColor-darkMode w-70 h-full shadow-xl z-50 overflow-y-auto">
                        <div className="flex justify-end p-3 pr-[10px]">
                            <button onClick={() => setRightSidebarOpen(false)}>
                                <img src="/assets/shared/close-grey.svg" alt="Close" className="w-5 h-5" />
                            </button>
                        </div>
                        <RightSidebar onClickOpenChat={onClickAction} isMobile={true} />
                    </div>
                </div>
            )}
            <RightSidebar
                onClickOpenChat={onClickAction}
                isCollapsed={isRightSidebarCollapsed}
                toggleCollapse={() => setIsRightSidebarCollapsed(!isRightSidebarCollapsed)}
            />
            {showSearchChat && (
                <NewChatModal
                    openModal={showSearchChat}
                    handleCloseModal={onClickShowModal}
                    onClickOpenChat={onTryingToOpenChat}
                />
            )}
        </div>
    );
};

export default Layout;
